import { useMemo } from 'react'
import { useClient } from 'wagmi'
import { useUserTicketsQuery } from '@/Overtime/queries/useUserTicketsQuery'
import type { SupportedNetwork } from '@/Overtime/types/network'
import useChainProvider from './useChainProvider'

interface UseUnredeemedOvertimeTicketsProps {
  address: string | undefined
  enabled?: boolean
}

interface UseUnredeemedOvertimeTicketsReturn {
  data: {
    inBets: number
    toPayout: number
    totalBets: number
    totalBetAmount: number
    totalPayoutAmount: number
  }
  isFetching: boolean
}

export const useUnredeemedOvertimeTickets = ({
  address,
  enabled = true,
}: UseUnredeemedOvertimeTicketsProps): UseUnredeemedOvertimeTicketsReturn => {
  const { isOvertimeChain, chainId } = useChainProvider()
  const client = useClient()

  const { data: overtimeTickets, isFetching } = useUserTicketsQuery(
    address || '',
    { networkId: chainId as SupportedNetwork, client },
    {
      enabled: enabled && !!address && isOvertimeChain,
      placeholderData: [],
    }
  )

  const { inBets, toPayout, totalBets, totalBetAmount, totalPayoutAmount } =
    useMemo(() => {
      if (!overtimeTickets) {
        return {
          inBets: 0,
          toPayout: 0,
          totalBets: 0,
          totalBetAmount: 0,
          totalPayoutAmount: 0,
        }
      }

      let inBets = 0
      let toPayout = 0
      let totalBets = overtimeTickets.length
      let totalBetAmount = 0
      let totalPayoutAmount = 0
      for (const ticket of overtimeTickets) {
        totalBetAmount += Number(ticket.buyInAmount || 0)
        if (!ticket.isResolved) {
          inBets += Number(ticket.buyInAmount || 0)
        }

        if (ticket.isResolved && ticket.isUserTheWinner) {
          totalPayoutAmount += Number(ticket.payout || 0)
        }
        if (ticket.isClaimable) {
          toPayout += Number(ticket.payout || 0)
        }
      }

      return { inBets, toPayout, totalBets, totalBetAmount, totalPayoutAmount }
    }, [overtimeTickets, isOvertimeChain])

  return {
    data: {
      inBets,
      toPayout,
      totalBets,
      totalBetAmount,
      totalPayoutAmount,
    },
    isFetching,
  }
}
